import api from './api';
import { ApiResponse, PageResponse } from '../types/api';
import { 
  PurchaseRequestDTO, 
  PurchaseResponseDTO, 
  PurchaseFilters,
  PurchaseStats
} from '../types/purchase';

const baseUrl = '/v1/compras';

export const purchaseService = {
  // Listar compras com paginação
  async list(params?: PurchaseFilters & { page?: number; size?: number; sort?: string }): Promise<ApiResponse<PageResponse<PurchaseResponseDTO>>> {
    const searchParams = new URLSearchParams();
    
    if (params?.page !== undefined) searchParams.append('page', params.page.toString());
    if (params?.size !== undefined) searchParams.append('size', params.size.toString());
    if (params?.sort) searchParams.append('sort', params.sort);
    
    // Filtros
    if (params?.fornecedorId) searchParams.append('fornecedorId', params.fornecedorId);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.dataInicio) searchParams.append('dataInicio', params.dataInicio);
    if (params?.dataFim) searchParams.append('dataFim', params.dataFim);
    if (params?.numeroNotaFiscal) searchParams.append('numeroNotaFiscal', params.numeroNotaFiscal);
    
    const queryString = searchParams.toString();
    const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;
    
    const response = await api.get<ApiResponse<PageResponse<PurchaseResponseDTO>>>(url);
    return response.data;
  },

  // Buscar compra por ID
  async getById(id: string): Promise<ApiResponse<PurchaseResponseDTO>> {
    const response = await api.get<ApiResponse<PurchaseResponseDTO>>(`${baseUrl}/${id}`);
    return response.data;
  },

  // Criar nova compra
  async create(data: PurchaseRequestDTO): Promise<ApiResponse<PurchaseResponseDTO>> {
    const response = await api.post<ApiResponse<PurchaseResponseDTO>>(baseUrl, data);
    return response.data;
  },

  // Atualizar compra
  async update(id: string, data: PurchaseRequestDTO): Promise<ApiResponse<PurchaseResponseDTO>> {
    const response = await api.put<ApiResponse<PurchaseResponseDTO>>(`${baseUrl}/${id}`, data);
    return response.data;
  },

  // Excluir compra
  async delete(id: string): Promise<ApiResponse<void>> {
    const response = await api.delete<ApiResponse<void>>(`${baseUrl}/${id}`);
    return response.data;
  },

  // Confirmar compra
  async confirm(id: string): Promise<ApiResponse<PurchaseResponseDTO>> {
    const response = await api.post<ApiResponse<PurchaseResponseDTO>>(`${baseUrl}/${id}/finalizar`);
    return response.data;
  },

  // Marcar como entregue
  async markAsDelivered(id: string): Promise<ApiResponse<PurchaseResponseDTO>> {
    const response = await api.patch<ApiResponse<PurchaseResponseDTO>>(`${baseUrl}/${id}/entregar`);
    return response.data;
  },

  // Cancelar compra (requer motivo como parâmetro de query)
  async cancel(id: string, motivo: string): Promise<ApiResponse<PurchaseResponseDTO>> {
    const params = new URLSearchParams();
    params.append('motivo', motivo);
    const response = await api.post<ApiResponse<PurchaseResponseDTO>>(`${baseUrl}/${id}/cancelar?${params.toString()}`);
    return response.data;
  },

  // Buscar compras por fornecedor
  async getBySupplier(fornecedorId: string, params?: { page?: number; size?: number }): Promise<ApiResponse<PageResponse<PurchaseResponseDTO>>> {
    const searchParams = new URLSearchParams();
    searchParams.append('fornecedorId', fornecedorId);
    
    if (params?.page !== undefined) searchParams.append('page', params.page.toString());
    if (params?.size !== undefined) searchParams.append('size', params.size.toString());
    
    const response = await api.get<ApiResponse<PageResponse<PurchaseResponseDTO>>>(`${baseUrl}?${searchParams.toString()}`);
    return response.data;
  },

  // Buscar compras por status
  async getByStatus(status: string, params?: { page?: number; size?: number }): Promise<ApiResponse<PageResponse<PurchaseResponseDTO>>> {
    const searchParams = new URLSearchParams();
    searchParams.append('status', status);
    
    if (params?.page !== undefined) searchParams.append('page', params.page.toString());
    if (params?.size !== undefined) searchParams.append('size', params.size.toString());
    
    const response = await api.get<ApiResponse<PageResponse<PurchaseResponseDTO>>>(`${baseUrl}?${searchParams.toString()}`);
    return response.data;
  },

  // Buscar compras pendentes
  async getPending(params?: { page?: number; size?: number }): Promise<ApiResponse<PageResponse<PurchaseResponseDTO>>> {
    return this.getByStatus('PENDENTE', params);
  },

  // Buscar estatísticas de compras
  async getStats(): Promise<ApiResponse<PurchaseStats>> {
    const response = await api.get<ApiResponse<PurchaseStats>>(`${baseUrl}/estatisticas`);
    return response.data;
  },

  // Pesquisar compras
  async search(searchTerm: string, params?: { page?: number; size?: number }): Promise<ApiResponse<PageResponse<PurchaseResponseDTO>>> {
    const searchParams = new URLSearchParams();
    searchParams.append('q', searchTerm);
    
    if (params?.page !== undefined) searchParams.append('page', params.page.toString());
    if (params?.size !== undefined) searchParams.append('size', params.size.toString());
    
    const response = await api.get<ApiResponse<PageResponse<PurchaseResponseDTO>>>(`${baseUrl}?${searchParams.toString()}`);
    return response.data;
  }
};

export default purchaseService;